import { Routes } from "./routes"
import { Navigate, createBrowserRouter, useParams } from "react-router-dom"
import HeaderOnlyLayout from "@/components/layouts/HeaderOnly"
import HeaderLeftNavbarMain from "@/components/layouts/HeaderLeftNavbarMain"
import LoginPage from "@/pages/Login"
import ForgotPasswordPage from "@/pages/ForgotPassword"
import ForgotPasswordForm from "@/pages/ForgotPassword/features/ForgotPasswordForm"
import ForgotPasswordSent from "@/pages/ForgotPassword/features/ForgotPasswordSent"
import NewPasswordForm from "@/pages/ForgotPassword/features/NewPasswordForm"
import ForgotPasswordDone from "@/pages/ForgotPassword/features/ForgotPasswordDone"
import OrganizationsPage from "@/pages/Organizations"
import HeaderMain from "@/components/layouts/HeaderMain"
import RequireAuth from "@/components/providers/RequireAuthProvider"
import UserSettingsPage from "@/pages/UserSettings"
import CampaignsPage from "@/pages/Campaigns"
import DashboardPage from "@/pages/Dashboard"
import SetupPasswordPage from "@/pages/SetupPassword"
import ConfirmEmailPage from "@/pages/ConfirmEmail"
import OrganizationPage from "@/pages/Organization"
import SendersPage from "@/pages/Senders"
import SequencesPage from "@/pages/Sequences"
import AddEditSequencePage from "@/pages/AddEditSequence"
import MasterListsPage from "@/pages/MasterLists"
import MasterListDetailsPage from "@/pages/MasterListDetails"
import AddEditCampaignPage from "@/pages/AddEditCampaign"
import DomainsPage from "@/pages/Domains"
import InboxingDomainsPage from "@/pages/InboxingDomains"
import AddEditDomainPage from "@/pages/AddEditDomain"
import EmailAddressesPage from "@/pages/EmailAddresses"
import ProspectsPage from "@/pages/Prospects"
import DNCPage from "@/pages/DNC"
import SpamTestingPage from "@/pages/SpamTesting"
import RequireAdminProvider from "@/components/providers/RequireAdminProvider"
import AdminPage from "@/pages/Admin"
import GsuitePage from "@/pages/Gsuite"
import AdminUsersPage from "@/pages/AdminUsers"
import AdminSpamTestingPage from "@/pages/AdminSpamTesting"
import AdminDNCPage from "@/pages/AdminDNC"
import ReplyClassificationPage from "@/pages/ReplyClassification"
import AdminSpamTestSettingsPage from "@/pages/AdminSpamTestSettings"
import SearchCriteriaPage from "@/pages/SearchCriteria"
import AddEditSearchCriteriaPage from "@/pages/AddEditSearchCriteria"
import AdminCookiesPage from "@/pages/AdminCookies"
import AdminProxyPage from "@/pages/AdminProxy"
import OAuthRedirectPage from "@/pages/OAuthRedirect"
import InboxPage from "@/pages/Inbox"
import AccessBlockedPage from "@/pages/AccessBlocked"
import CheckPageAccess from "@/components/providers/CheckPageAccess"
import AdminDashboardPage from "@/pages/AdminDashboard"
import { useAuth } from "@/hooks/useAuth"
import { useOrganizationPermissions } from "@/hooks/useOrganizationPermissions"
import Loader from "@/components/Loader"
import type { ReactElement } from "react"

const LegacySetupRoute = ({ children }: { children: ReactElement }) => {
  const { id } = useParams()
  const { user, isLoading } = useAuth()
  const {
    accessiblePages,
    hasPageAccess: hasInboxingAccess,
    isLoading: permissionsLoading,
  } = useOrganizationPermissions({ page: "inboxing-domains" })

  if (
    isLoading ||
    user === undefined ||
    (!user?.isAdmin && permissionsLoading)
  ) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    )
  }

  if (!user?.isAdmin) {
    const fallbackPage =
      accessiblePages.find((page) => page === "/dashboard") ||
      accessiblePages.find(
        (page) => !["/domains", "/inboxing-domains"].includes(page),
      )
    const fallbackRoute =
      id && fallbackPage
        ? `/organization/${id}/${fallbackPage.replace(/^\//, "")}`
        : Routes.organizations

    return (
      <Navigate
        to={
          id && hasInboxingAccess
            ? `/organization/${id}/inboxing-domains`
            : fallbackRoute
        }
        replace
      />
    )
  }

  return children
}

const AdminOnlyOrganizationRoute = ({
  children,
}: {
  children: ReactElement
}) => {
  const { id } = useParams()
  const { user, isLoading } = useAuth()

  if (isLoading || user === undefined) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    )
  }

  if (!user?.isAdmin) {
    return (
      <Navigate
        to={id ? `/organization/${id}/dashboard` : Routes.organizations}
        replace
      />
    )
  }

  return children
}

export const router = createBrowserRouter([
  {
    path: Routes.login,
    element: (
      <HeaderOnlyLayout>
        <LoginPage />
      </HeaderOnlyLayout>
    ),
  },
  {
    path: Routes.passwordSetup,
    element: (
      <HeaderOnlyLayout>
        <SetupPasswordPage />
      </HeaderOnlyLayout>
    ),
  },
  {
    path: Routes.confirmEmail,
    element: (
      <HeaderOnlyLayout>
        <ConfirmEmailPage />
      </HeaderOnlyLayout>
    ),
  },
  {
    path: Routes.forgotPassword.index,
    element: (
      <HeaderOnlyLayout>
        <ForgotPasswordPage />
      </HeaderOnlyLayout>
    ),
    children: [
      {
        path: "form",
        element: <ForgotPasswordForm />,
      },
      {
        path: "email-sent",
        element: <ForgotPasswordSent />,
      },
      {
        path: "new-password",
        element: <NewPasswordForm />,
      },
      {
        path: "done",
        element: <ForgotPasswordDone />,
      },
    ],
  },
  {
    path: Routes.organizations,
    element: (
      <RequireAuth>
        <HeaderMain>
          <OrganizationsPage />
        </HeaderMain>
      </RequireAuth>
    ),
  },
  {
    path: "/admin-dashboard",
    element: (
      <RequireAdminProvider>
        <HeaderMain>
          <AdminDashboardPage />
        </HeaderMain>
      </RequireAdminProvider>
    ),
  },
  {
    path: Routes.userSettings,
    element: (
      <RequireAuth>
        <HeaderMain>
          <UserSettingsPage />
        </HeaderMain>
      </RequireAuth>
    ),
  },
  {
    path: Routes.accessBlocked,
    element: (
      <RequireAuth>
        <HeaderMain>
          <AccessBlockedPage />
        </HeaderMain>
      </RequireAuth>
    ),
  },
  {
    path: Routes.organization.index,
    element: (
      <RequireAuth>
        <HeaderLeftNavbarMain>
          <OrganizationPage />
        </HeaderLeftNavbarMain>
      </RequireAuth>
    ),
    children: [
      {
        element: <CheckPageAccess />,
        children: [
          {
            path: "dashboard",
            element: <DashboardPage />,
          },
          {
            path: "campaigns",
            element: <CampaignsPage />,
          },
          {
            path: "campaigns/create",
            element: <AddEditCampaignPage />,
          },
          {
            path: "campaigns/edit/:campaignId",
            element: <AddEditCampaignPage />,
          },
          {
            path: "senders",
            element: <SendersPage />,
          },
          {
            path: "sequences",
            element: <SequencesPage />,
          },
          {
            path: "sequences/create",
            element: <AddEditSequencePage />,
          },
          {
            path: "sequences/edit/:sequenceId",
            element: <AddEditSequencePage />,
          },
          {
            path: "master-lists",
            element: <MasterListsPage />,
          },
          {
            path: "master-lists/:masterListId",
            element: <MasterListDetailsPage />,
          },
          {
            path: "domains",
            element: (
              <LegacySetupRoute>
                <DomainsPage />
              </LegacySetupRoute>
            ),
          },
          {
            path: "inboxing-domains",
            element: <InboxingDomainsPage />,
          },
          {
            path: "domains/create",
            element: (
              <LegacySetupRoute>
                <AddEditDomainPage />
              </LegacySetupRoute>
            ),
          },
          {
            path: "domains/edit/:domainId",
            element: (
              <LegacySetupRoute>
                <AddEditDomainPage />
              </LegacySetupRoute>
            ),
          },
          {
            path: "emails",
            element: (
              <LegacySetupRoute>
                <EmailAddressesPage />
              </LegacySetupRoute>
            ),
          },
          {
            path: "prospects",
            element: <ProspectsPage />,
          },
          {
            path: "dnc",
            element: <DNCPage />,
          },
          {
            path: "spam-testing",
            element: (
              <AdminOnlyOrganizationRoute>
                <SpamTestingPage />
              </AdminOnlyOrganizationRoute>
            ),
          },
          {
            path: "search-criteria",
            element: <SearchCriteriaPage />,
          },
          {
            path: "search-criteria/add",
            element: <AddEditSearchCriteriaPage />,
          },
          {
            path: "search-criteria/edit/:criteriaId",
            element: <AddEditSearchCriteriaPage />,
          },
          {
            path: "inbox",
            element: (
              <AdminOnlyOrganizationRoute>
                <InboxPage />
              </AdminOnlyOrganizationRoute>
            ),
          },
        ],
      },
    ],
  },
  {
    path: Routes.admin.index,
    element: (
      <RequireAdminProvider>
        <HeaderMain>
          <AdminPage />
        </HeaderMain>
      </RequireAdminProvider>
    ),
    children: [
      {
        path: "domainAdmins",
        element: <GsuitePage />,
      },
      {
        path: "users",
        element: <AdminUsersPage />,
      },
      {
        path: "spam",
        element: <AdminSpamTestingPage />,
      },
      {
        path: "dnc",
        element: <AdminDNCPage />,
      },
      {
        path: "ai",
        element: <ReplyClassificationPage />,
      },
      {
        path: "spam-test-settings",
        element: <AdminSpamTestSettingsPage />,
      },
      {
        path: "cookies",
        element: <AdminCookiesPage />,
      },
      {
        path: "proxy",
        element: <AdminProxyPage />,
      },
    ],
  },
  {
    path: "/",
    element: <Navigate to={Routes.organizations} replace />,
  },
  {
    path: "oauth",
    element: <OAuthRedirectPage />,
  },
  {
    path: Routes.auth.inboxingCallback,
    element: <OAuthRedirectPage />,
  },
  {
    path: "*",
    element: <div>Not Found 404</div>,
  },
])
